import type { ReactNode } from 'react';

import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import { useGraphQLQueries } from '../../../hooks/useGraphQLQueries.hook';
import { CostPage_OneCost_QueryDocument } from '../../../pages/Cost/hooks/useFindOneCostQuery.hook';
import { MaterialPage_OneMaterial_QueryDocument } from '../../../pages/Material/hooks/useFindOneMaterialQuery.hook';

import type { ServiceUsageCardCmp_Service } from './fragments/service.fragment';
import type { ServiceUsageCardCmp_ResolvableServiceBaseInterface } from './fragments/resolvable-service.fragment';

interface Props {
  readonly expanded: boolean;
  readonly quotationSettings: ReactNode;
  readonly service:
    | ServiceUsageCardCmp_Service
    | ServiceUsageCardCmp_ResolvableServiceBaseInterface;
}

export const MoreDetails = ({
  expanded,
  quotationSettings,
  service,
}: Readonly<Props>) => {
  const { costUsages, materialUsages } = service;
  const costIds = costUsages.map((usage) => usage.costId);
  const materialIds = materialUsages.map((usage) => usage.materialId);

  const costResults = useGraphQLQueries(
    costIds.map((costId) => ({
      document: CostPage_OneCost_QueryDocument,
      variables: { id: costId },
      useQueryOptions: { enabled: !!costId },
    })),
  );

  const materialResults = useGraphQLQueries(
    materialIds.map((materialId) => ({
      document: MaterialPage_OneMaterial_QueryDocument,
      variables: { id: materialId },
      useQueryOptions: { enabled: !!materialId },
    })),
  );

  const areCostsSuccessful = costResults.every((result) => result.isSuccess);
  const areMaterialsSuccessful = materialResults.every(
    (result) => result.isSuccess,
  );

  const skeleton = (
    <Skeleton sx={{ display: 'inline-block' }} variant="text" width={120} />
  );

  return (
    <Collapse in={expanded} timeout="auto" unmountOnExit>
      <CardContent>
        {quotationSettings}
        <Typography color="text.secondary" variant="overline">
          Coûts Associés
        </Typography>
        <Typography gutterBottom variant="body2">
          {!costIds.length
            ? 'Aucun coût associé à ce service.'
            : areCostsSuccessful
              ? costResults
                  .map(
                    (costResult) =>
                      `${costResult.data.findOneCost.code} - ${costResult.data.findOneCost.label}`,
                  )
                  .join(', ')
              : skeleton}
        </Typography>
        <Typography color="text.secondary" variant="overline">
          Matériels Associés
        </Typography>
        <Typography gutterBottom variant="body2">
          {!materialIds.length
            ? 'Aucun matériel associé à ce service.'
            : areMaterialsSuccessful
              ? materialResults
                  .map(
                    (materialResult) =>
                      `${materialResult.data.findOneMaterial.code} - ${materialResult.data.findOneMaterial.label}`,
                  )
                  .join(', ')
              : skeleton}
        </Typography>
      </CardContent>
    </Collapse>
  );
};
